import { addDays, format, parse } from 'date-fns';
import { ErrorMessage, Field, FieldArray } from 'formik';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Button, Col, Form, Row, Spinner, Stack } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { DateFilterField } from '../../../../../../components/tables/filterForms/DateFilterField';
import { TimeField } from '../../../../../../components/tables/filterForms/TimeField';
import { FormLabel, FormStepHeader } from '../../../../../../components/forms';
import { useToast } from '../../../../../../context/ToastContext';
import { useGetThemeQuery } from '../../../../../../services/endpoints/content/theme';
import { useGetUsersQuery, User } from '../../../../../../services/endpoints/people/user';
import { dateInTimeZone, formatDate } from '../../../../../../utils/dateUtils';
import { getErrorMessage } from '../../../../../../utils/utils';
import { StepProps } from '../AddEditSession';
import { normalizeThemeLessonPlans, buildVirtualAssignments, FeaturedLessonPlanItem } from '../../../../../../utils/lessonPlanUtils';
const MAX_DAYS = 365; // Prevent runaway loops

export const ClassesForm = ({ touched, errors, values, setFieldValue, readOnly }: StepProps) => {
    const { data, error, isLoading } = useGetUsersQuery({
        scope: 'colleagues',
        data: { entries: 1000, orderBy: { field: 'first_name', order: 'asc' } }
    });
    const {
        data: theme,
        error: themeError,
        isLoading: themeLoading
    } = useGetThemeQuery(+(values?.theme_id ?? 0), { skip: !values?.theme_id });
    const {addToast} = useToast();

    const themePlans = useMemo(() => normalizeThemeLessonPlans(theme?.theme), [theme?.theme]);

    // Track if classes were manually modified to prevent regeneration
    const classesManuallyModified = useRef(false);

    // Dates this effect filled in. Anything not in here was picked by a human.
    const autoAssignedDates = useRef<Set<string>>(new Set());

    // Show error toast
    useEffect(() => {
        if (error || themeError) {
            addToast(getErrorMessage(error ?? themeError), 'error');
        }
    }, [error, themeError, addToast]);

    // Memoize stable `setFieldValue` method to avoid unnecessary effect triggers
    const stableSetFieldValue = useCallback(
        (field: string, value: any, shouldValidate?: boolean) => {
            setFieldValue?.(field, value, shouldValidate);
        },
        [setFieldValue]
    );

    // Generate classes based on schedule values
    useEffect(() => {
        if (
            values?.id ||
            classesManuallyModified.current ||
            !values?.starts_on ||
            !values?.ends_on ||
            !values?.day_of_week?.length ||
            !setFieldValue
        ) {
            return;
        }

        const endDate = parse(values.ends_on, 'yyyy-MM-dd', new Date());
        let date = parse(values.starts_on, 'yyyy-MM-dd', new Date());
        const classes = [];

        const existingClassesByDate = new Map(
            values.classes
                ?.filter(
                    (c) =>
                        !c.destroy &&
                        (c.notes ||
                            c.lesson_plan_ids?.length ||
                            c.instructor_ids?.length ||
                            c.trainee_ids?.length ||
                            c.observer_ids?.length)
                )
                .map((c) => [c.date, c]) ?? []
        );

        let iterations = 0;
        // Calendar dates only — use date-fns format, not formatDate (account TZ).
        // formatDate shifts local midnight into the previous civil day when the
        // profile TZ is west of the browser (or after DST), which drops the last
        // matching class in the range.
        while (date <= endDate && iterations < MAX_DAYS) {
            iterations++;
            const dateStr = format(date, 'yyyy-MM-dd');

            if (
                values.day_of_week.includes(format(date, 'EEEE').slice(0, 3)) &&
                !values.no_class?.includes(dateStr)
            ) {
                const existingClass = existingClassesByDate.get(dateStr);
                if (existingClass) {
                    classes.push(existingClass);
                } else {
                    classes.push({
                        date: dateStr,
                        starts_at: values.starts_at,
                        ends_at: values.ends_at,
                        times_virtual: true,
                        destroy: false
                    });
                }
            }
            date = addDays(date, 1);
        }

        const sortedClasses = [...classes].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

        if (JSON.stringify(sortedClasses) !== JSON.stringify(values.classes)) {
            stableSetFieldValue('classes', sortedClasses);
        }
    }, [
        values?.starts_on,
        values?.ends_on,
        values?.day_of_week,
        values?.no_class,
        stableSetFieldValue
    ]);

    // Lesson plan assignment. Mirrors the server virtualizer; see
    // lessonPlanUtils.buildVirtualAssignments for the rules.
    // Runs on create and edit for ASE themes. Virtual / auto-filled cards
    // follow the virtualizer when dates change; manual overrides stay put.
    useEffect(() => {
        if (
            !(theme?.theme as any)?.is_ase ||
            !values?.classes?.length ||
            !themePlans.length
        ) return;

        const active = values.classes.filter((c) => String(c.destroy) !== 'true');
        const assignments = buildVirtualAssignments(
            active.map((c) => c.date),
            themePlans as FeaturedLessonPlanItem[],
        );
        if (!assignments.size) return;

        const filled = new Set<string>();
        const updatedClasses = values.classes.map((cls) => {
            if (String(cls.destroy) === 'true') return cls;
            const day = cls.date.slice(0, 10);

            // Sticky: manual picks, clears, and legacy pinned cards.
            if (cls.lesson_plans_virtual === false) return cls;

            // Re-derive empty cards, virtual cards, and dates we auto-filled.
            const canReassign =
                cls.lesson_plans_virtual === true ||
                !cls.lesson_plan_ids?.length ||
                autoAssignedDates.current.has(day);
            if (cls.lesson_plan_ids?.length && !canReassign) return cls;

            const lp = assignments.get(day);
            if (!lp) return cls;
            filled.add(day);
            return {
                ...cls,
                lesson_plan_ids: [{ id: String(lp.id), label: lp.name }],
                lesson_plans_virtual: true,
            };
        });

        autoAssignedDates.current = filled;

        if (JSON.stringify(updatedClasses) !== JSON.stringify(values.classes)) {
            stableSetFieldValue('classes', updatedClasses);
        }
    }, [themePlans, values?.classes, stableSetFieldValue]);

    // Session time changes propagate to every class still inheriting it. Pinned
    // cards (times_virtual === false) keep their own time. Mirrors v2, where the
    // lesson fell back to course.starts_at unless it had an explicit override.
    useEffect(() => {
        if (!values?.classes?.length || !setFieldValue) return;
        if (!values.starts_at || !values.ends_at) return;

        let changed = false;
        const updated = values.classes.map((cls) => {
            if (cls.times_virtual === false) return cls;
            if (cls.starts_at === values.starts_at && cls.ends_at === values.ends_at) return cls;
            changed = true;
            return { ...cls, starts_at: values.starts_at, ends_at: values.ends_at };
        });

        if (changed) stableSetFieldValue('classes', updated);
    }, [values?.starts_at, values?.ends_at, values?.classes, stableSetFieldValue]);

    // Memoize instructor options to prevent unnecessary recalculations
    const instructorOptions = useMemo(
        () =>
            (data?.results.map((instructor: User) => ({
                id: String(instructor.id),
                label: `${instructor.first_name} ${instructor.last_name}`
            })) ?? []).sort((a, b) => a.label.localeCompare(b.label)),
        [data?.results]
    );

    // Memoize lesson plan options
    const lessonPlansOptions = useMemo(
        () =>
            themePlans
                .map((lp) => ({ label: lp.name, id: lp.id }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [themePlans]
    );

    // Handle manual class modifications
    const handleFieldChange = useCallback(
        (field: string, value: any) => {
            classesManuallyModified.current = true;
            setFieldValue?.(field, value);
        },
        [setFieldValue]
    );

    const handleRemoveClass = useCallback(
        (idx: number, value: any, remove: (idx: number) => void) => {
            classesManuallyModified.current = true;
            if (value.id) {
                setFieldValue?.(`classes.${idx}.destroy`, true);
            } else {
                remove(idx);
            }
        },
        [setFieldValue]
    );

    const handleAddClass = useCallback(
        (push: (obj: any) => void) => {
            classesManuallyModified.current = true;
            const lastClass = values?.classes?.[values.classes.length - 1];
            const newClass = {
                date: lastClass?.date ?? formatDate(dateInTimeZone(), 'yyyy-MM-dd'),
                starts_at: values?.starts_at,
                ends_at: values?.ends_at,
                times_virtual: true,
                destroy: false
            };
            push(newClass);
            // Re-sort after push so the new class lands in date order
            const sorted = [...(values?.classes ?? []), newClass].sort((a, b) =>
                a.date < b.date ? -1 : a.date > b.date ? 1 : 0
            );
            setFieldValue?.('classes', sorted);
        },
        [values?.classes, values?.starts_at, values?.ends_at, setFieldValue]
    );

    if (isLoading || themeLoading) {
        return (
            <div className="w-100 vh-100 d-inline-flex">
                <Spinner
                    variant="primary"
                    className="m-auto"
                />
            </div>
        );
    }

    return (
        <Stack gap={3}>
            <FormStepHeader title="Classes" showRequiredLegend={!readOnly} className="mb-0" />

            {!readOnly && (
                <Form.Text>
                    *Start and end times, if specified, will override the standard class time for that day.
                </Form.Text>
            )}

            <FieldArray name="classes">
                {({ remove, push }) => (
                    <div>
                        <Row>
                            {values?.classes?.map((value, idx) =>
                                    String(value.destroy) !== 'true' ? (
                                        <Col
                                            key={value.id ?? `temp-${value.date}-${idx}`}
                                            xs={12}
                                            md={6}
                                            lg={4}
                                            className="mb-3"
                                        >
                                            <div style={{ border: '1px solid #dee2e6', borderRadius: 4 }}>
                                                <div
                                                    className="d-flex align-items-center justify-content-between px-3 py-2"
                                                    style={{ borderBottom: '1px solid #dee2e6' }}
                                                >
                                                    <span className="text-secondary" style={{ fontSize: '.875rem' }}>{`Class #${idx + 1} details`}</span>
                                                </div>
                                                <div className="p-3">
                                                    <Stack gap={2}>
                                                        <Form.Group controlId={`classes.${idx}.date`}>
                                                            <DateFilterField
                                                                name={`classes.${idx}.date`}
                                                                label="Date"
                                                                defaultValue={value.date || undefined}
                                                                disabled={readOnly}
                                                                required
                                                            />
                                                        </Form.Group>

                                                        <Row>
                                                            <Col
                                                                xs={12}
                                                                lg={6}
                                                            >
                                                                <Form.Group controlId={`classes.${idx}.starts_at`}>
                                                                    <TimeField
                                                                        name={`classes.${idx}.starts_at`}
                                                                        label="Start Time"
                                                                        disabled={readOnly}
                                                                        onValueChange={() =>
                                                                            handleFieldChange(`classes.${idx}.times_virtual`, false)
                                                                        }
                                                                        required
                                                                    />
                                                                </Form.Group>
                                                            </Col>
                                                            <Col
                                                                xs={12}
                                                                lg={6}
                                                            >
                                                                <Form.Group controlId={`classes.${idx}.ends_at`}>
                                                                    <TimeField
                                                                        name={`classes.${idx}.ends_at`}
                                                                        label="End Time"
                                                                        disabled={readOnly}
                                                                        onValueChange={() =>
                                                                            handleFieldChange(`classes.${idx}.times_virtual`, false)
                                                                        }
                                                                        required
                                                                    />
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>

                                                        <Form.Group controlId={`classes.${idx}.notes`}>
                                                            <FormLabel>Notes</FormLabel>
                                                            <Field
                                                                name={`classes.${idx}.notes`}
                                                                type="text"
                                                                as={Form.Control}
                                                                disabled={readOnly}
                                                            />
                                                        </Form.Group>

                                                        <Form.Group controlId={`classes.${idx}.lesson_plan_ids`}>
                                                            <FormLabel>Lesson Plan</FormLabel>
                                                            <Typeahead
                                                                id={`classes.${idx}.lesson_plan_ids`}
                                                                multiple
                                                                disabled={readOnly}
                                                                flip
                                                                positionFixed
                                                                placeholder={readOnly ? '' : 'Type here'}
                                                                options={lessonPlansOptions}
                                                                selected={values.classes[idx].lesson_plan_ids}
                                                                onChange={(selected) => {
                                                                    const day = values.classes[idx].date.slice(0, 10);
                                                                    if (selected.length) {
                                                                        // Manual override — sticky until cleared.
                                                                        autoAssignedDates.current.delete(day);
                                                                        setFieldValue?.(`classes.${idx}.lesson_plans_virtual`, false);
                                                                    } else {
                                                                        // Clear restores theme virtualization.
                                                                        setFieldValue?.(`classes.${idx}.lesson_plans_virtual`, true);
                                                                    }
                                                                    handleFieldChange(`classes.${idx}.lesson_plan_ids`, selected);
                                                                }}
                                                            />
                                                        </Form.Group>

                                                        <Form.Group controlId={`classes.${idx}.instructor_ids`}>
                                                            <FormLabel>Additional</FormLabel>
                                                            <Typeahead
                                                                id={`classes.${idx}.instructor_ids`}
                                                                multiple
                                                                disabled={readOnly}
                                                                flip
                                                                positionFixed
                                                                placeholder={readOnly ? '' : 'Type here'}
                                                                options={instructorOptions}
                                                                selected={values.classes[idx].instructor_ids}
                                                                onChange={(selected) =>
                                                                    handleFieldChange(`classes.${idx}.instructor_ids`, selected)
                                                                }
                                                            />
                                                        </Form.Group>

                                                        <Form.Group controlId={`classes.${idx}.trainee_ids`}>
                                                            <FormLabel>Trainees</FormLabel>
                                                            <Typeahead
                                                                id={`classes.${idx}.trainee_ids`}
                                                                multiple
                                                                disabled={readOnly}
                                                                flip
                                                                positionFixed
                                                                placeholder={readOnly ? '' : 'Type here'}
                                                                options={instructorOptions}
                                                                selected={values.classes[idx].trainee_ids}
                                                                onChange={(selected) =>
                                                                    handleFieldChange(`classes.${idx}.trainee_ids`, selected)
                                                                }
                                                            />
                                                        </Form.Group>

                                                        <Form.Group controlId={`classes.${idx}.observer_ids`}>
                                                            <FormLabel>Observers</FormLabel>
                                                            <Typeahead
                                                                id={`classes.${idx}.observer_ids`}
                                                                multiple
                                                                disabled={readOnly}
                                                                flip
                                                                positionFixed
                                                                placeholder={readOnly ? '' : 'Type here'}
                                                                options={instructorOptions}
                                                                selected={values.classes[idx].observer_ids}
                                                                onChange={(selected) =>
                                                                    handleFieldChange(`classes.${idx}.observer_ids`, selected)
                                                                }
                                                            />
                                                        </Form.Group>

                                                        {!readOnly && (
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                className="btn-box-shadow text-uppercase border-0"
                                                                style={{ width: 84, background: '#B81F69', fontSize: '.75rem' }}
                                                                onClick={() => handleRemoveClass(idx, value, remove)}
                                                            >
                                                                Remove
                                                            </Button>
                                                        )}
                                                    </Stack>
                                                </div>
                                            </div>
                                        </Col>
                                    ) : null
                            )}
                        </Row>

                        {!readOnly && (
                            <Button
                                variant="primary"
                                size="sm"
                                className="btn-box-shadow text-uppercase border-0"
                                style={{ width: 152, fontSize: '.75rem', borderBottomLeftRadius: 8 }}
                                onClick={() => handleAddClass(push)}
                            >
                                + Add New Class
                            </Button>
                        )}
                    </div>
                )}
            </FieldArray>

            <Form.Group>
                <Field
                    as={Form.Floating}
                    isInvalid={touched.classes && !!errors.classes}
                />
                <ErrorMessage name="classes">
                    {(msg) => {
                        let errMsg = 'ERROR: ';
                        if (Array.isArray(msg)) {
                            (msg as unknown as { [key: string]: string }[]).forEach((msg, idx) => {
                                if (msg) {
                                    errMsg += `Class #${idx + 1} (${Object.values(msg).join(', ')}); `;
                                }
                            });
                        } else if (typeof msg === 'string') {
                            errMsg = msg;
                        } else {
                            errMsg = JSON.stringify(msg);
                        }
                        return <span className="text-danger fs-small">{errMsg}</span>;
                    }}
                </ErrorMessage>
            </Form.Group>
        </Stack>
    );
};